/**
 * F-5 — the operator-facing half of the geometry fallback.
 *
 * `@genz/engine-bind`'s `geometry.repairGeometry` does the maths (clip a cutout back inside
 * the print area, drop one that subtracts nothing, clamp a print area that runs off the
 * image). This module turns its counts into the sentences the banner and the notice show, so
 * every surface says the SAME thing about the same repair. One module, one wording.
 */
import { geometry } from "@genz/engine-bind";
import type { Polygon, ValidationResult } from "@genz/engine-bind";

export type GeometryRepair = ReturnType<typeof geometry.repairGeometry>;

/** Plural-aware "N cutouts". */
function cutoutCount(n: number): string {
  return n === 1 ? "1 cutout" : `${n} cutouts`;
}

/**
 * What the repair DID, in one sentence. Only ever called when `repair.changed` — a repair that
 * changed nothing must not announce itself, or every detection would raise a banner.
 */
export function geometryRepairMessage(repair: GeometryRepair): string {
  const parts: string[] = [];
  if (repair.dropped > 0) {
    parts.push(`removed ${cutoutCount(repair.dropped)} that fell outside the print area`);
  }
  if (repair.clipped > 0) {
    parts.push(`trimmed ${cutoutCount(repair.clipped)} back inside the print area`);
  }
  if (repair.clampedPrintArea) {
    parts.push("pulled the print area back inside the image");
  }
  if (parts.length === 0) return "Auto-detect fixed the print-area geometry.";
  const list = parts.length === 1
    ? parts[0]
    : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  return `Auto-detect ${list}. Check the shapes on the canvas before you publish.`;
}

/**
 * The whole story of one auto-detect, in one notice: what the ENGINE decided not to emit, then
 * what the client-side repair changed. `null` when there is nothing to say.
 *
 * The engine half is `Detection.notes` (WS-DET F-4). A provider-supplied camera cutout that
 * falls outside the print area cannot be emitted — a cutout that cuts nothing is the defect the
 * detector exists to avoid — but dropping it in silence breaks the same rule the repair notice
 * exists for: the operator asked for a proposal and is entitled to know it lost a piece. The
 * repair `changed` nothing in that case, so without this the notice never appeared at all.
 */
export function detectionNoticeMessage(
  notes: string[] | undefined,
  repair: GeometryRepair,
): string | null {
  const parts: string[] = [];
  if (repair.changed) parts.push(geometryRepairMessage(repair));
  for (const note of notes ?? []) {
    const trimmed = note.trim();
    if (trimmed.length > 0) parts.push(`Auto-detect: ${trimmed}.`);
  }
  return parts.length > 0 ? parts.join(" ") : null;
}

/** True when `validation` is a real problem the operator must see. A view with NO print area
 *  yet is not an error — it is an empty editor (`CutoutToolsCard` says so in the neutral
 *  voice), and raising a red banner on a blank canvas would train the operator to ignore it. */
export function shouldShowGeometryBanner(printArea: Polygon, validation: ValidationResult): boolean {
  return printArea.length >= 3 && !validation.valid;
}
